import { JobStatus, PartFinderActivityEntry, PartFinderBatch } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useEffect, useRef, useState } from 'react';
import { UnifiedRow, isInProgress } from './activityRows';

const MAX_CHILDREN = 1000;

const batchChildSignature = (batch: PartFinderBatch): string => {
  const { completed, running, pending, failed, cancelled, total } = batch.breakdown;
  return `${batch.status}:${batch.progress}:${completed}:${running}:${pending}:${failed}:${cancelled}:${total}`;
};

type GridApiReference = ReturnType<typeof useGridApiRef>;

export const useBatchChildren = (entries: PartFinderActivityEntry[], tenantId: string, gridApi: GridApiReference) => {
  const [expandedBatchIds, setExpandedBatchIds] = useState<Set<string>>(new Set());
  const [childrenByBatch, setChildrenByBatch] = useState<Record<string, UnifiedRow[]>>({});
  const [loadingBatchIds, setLoadingBatchIds] = useState<Set<string>>(new Set());
  const [childFetchFailed, setChildFetchFailed] = useState(false);

  const expandedBatchIdsReference = useRef(expandedBatchIds);
  const loadingBatchIdsReference = useRef(loadingBatchIds);
  const previousBatchStatusReference = useRef<Map<string, JobStatus>>(new Map());
  const lastChildSignatureReference = useRef<Map<string, string>>(new Map());
  expandedBatchIdsReference.current = expandedBatchIds;
  loadingBatchIdsReference.current = loadingBatchIds;

  const collapseRow = (rowId: string) => {
    const node = gridApi.current?.getRowNode(rowId);
    if (node?.type === 'group') {
      gridApi.current?.setRowChildrenExpansion(rowId, false);
    }
  };

  const fetchChildren = async (rowId: string, batchUuid: string, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoadingBatchIds(previous => new Set(previous).add(rowId));
    }
    try {
      const response = await api.tenants.listPartFinderReports(tenantId, 1, MAX_CHILDREN, batchUuid);
      const childRows: UnifiedRow[] = response.data.partFinderReports.map(report => ({
        id: `report-${report.id}`,
        kind: 'report',
        path: [rowId, `report-${report.id}`],
        createdAt: report.createdAt,
        report,
      }));
      setChildrenByBatch(previous => ({ ...previous, [rowId]: childRows }));
      setChildFetchFailed(false);
    } catch {
      if (!options?.silent) {
        setChildFetchFailed(true);
        setExpandedBatchIds(previous => {
          const next = new Set(previous);
          next.delete(rowId);
          return next;
        });
        collapseRow(rowId);
      }
    } finally {
      if (!options?.silent) {
        setLoadingBatchIds(previous => {
          const next = new Set(previous);
          next.delete(rowId);
          return next;
        });
      }
    }
  };

  const toggleBatch = (rowId: string, batchUuid: string) => {
    const willExpand = !expandedBatchIds.has(rowId);
    if (willExpand && !childrenByBatch[rowId] && !loadingBatchIds.has(rowId)) {
      void fetchChildren(rowId, batchUuid);
    }
    setExpandedBatchIds(previous => {
      const next = new Set(previous);
      if (willExpand) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
    if (!willExpand) {
      collapseRow(rowId);
    }
  };

  const resetChildren = () => {
    for (const rowId of expandedBatchIdsReference.current) {
      collapseRow(rowId);
    }
    setChildrenByBatch({});
    setLoadingBatchIds(new Set());
    setExpandedBatchIds(new Set());
  };

  useEffect(() => {
    const nextStatuses = new Map<string, JobStatus>();
    for (const entry of entries) {
      if (entry.type !== 'batch') continue;
      const rowId = `batch-${entry.batch.id}`;
      const status = entry.batch.status;
      nextStatuses.set(entry.batch.id, status);

      const signature = batchChildSignature(entry.batch);
      const previousSignature = lastChildSignatureReference.current.get(entry.batch.id);
      lastChildSignatureReference.current.set(entry.batch.id, signature);

      const previousStatus = previousBatchStatusReference.current.get(entry.batch.id);
      const becameTerminal = previousStatus !== undefined && isInProgress(previousStatus) && !isInProgress(status);
      const changed = previousSignature !== undefined && signature !== previousSignature;

      if (
        (changed || becameTerminal) &&
        (isInProgress(status) || becameTerminal) &&
        expandedBatchIdsReference.current.has(rowId) &&
        !loadingBatchIdsReference.current.has(rowId)
      ) {
        void fetchChildren(rowId, entry.batch.id, { silent: true });
      }
    }
    previousBatchStatusReference.current = nextStatuses;
  }, [entries]);

  return {
    expandedBatchIds,
    loadingBatchIds,
    childrenByBatch,
    childFetchFailed,
    setChildFetchFailed,
    toggleBatch,
    resetChildren,
  };
};
